import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_URL } from '../api/base';
import { getLocalized } from '../utils/localized';

type LocText = { ru?: string; ky?: string; en?: string } | string | null;

interface Post {
  id: number;
  slug: string;
  title: LocText;
  excerpt: LocText;
  content: LocText;
  coverImage: string | null;
  publishedAt: string | null;
  category?: string | null;
  authorName?: string | null;
  authorRole?: string | null;
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState<Post | null>(null);
  const [related, setRelated] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await axios.get<{ post: Post }>(`${API_URL}/api/blog/${slug}`);
        setPost(data.post);
      } catch (err: any) {
        if (err.response?.status === 404) setNotFound(true);
        else console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ posts: Post[] }>(`${API_URL}/api/blog`, {
          params: { limit: 4 }
        });
        setRelated(data.posts.filter(p => p.slug !== slug).slice(0, 3));
      } catch {
        // ignore
      }
    })();
  }, [slug]);

  const title = (p: Post) => (typeof p.title === 'string' ? p.title : getLocalized(p.title, i18n.language));
  const excerptText = post
    ? typeof post.excerpt === 'string'
      ? post.excerpt
      : getLocalized(post.excerpt, i18n.language)
    : '';
  const rawContent = post
    ? typeof post.content === 'string'
      ? post.content
      : getLocalized(post.content, i18n.language)
    : '';
  const isHtmlContent = /<\w+[\s>]/.test(rawContent);
  const plainToHtml = (s: string) =>
    s
      .split(/\n\s*\n/) // split into paragraphs
      .map(chunk => {
        const escaped = chunk
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br/>')
          .trim();
        return escaped ? `<p>${escaped}</p>` : '';
      })
      .join('');
  const contentHtml = isHtmlContent ? rawContent : plainToHtml(rawContent);

  const fmtDate = (s: string | null) => {
    if (!s) return '';
    return new Date(s).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <>
      <Header />
      <main style={{ padding: '34px 24px 90px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#5a6b4d' }}>
            {t('loading', 'Загрузка…')}
          </div>
        ) : notFound || !post ? (
          <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', padding: '60px 0' }}>
            <h1 style={{ font: "700 28px 'Montserrat Alternates', sans-serif", color: '#2D5016' }}>
              {t('blog.not_found', 'Статья не найдена')}
            </h1>
            <div style={{ marginTop: 20 }}>
              <Link to="/blog" className="back-btn">
                ← {t('blog.back_to_list', 'Все статьи')}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <article style={{ maxWidth: 760, margin: '0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
                <Link to="/blog" className="back-btn">
                  ← {t('blog.back', 'Назад к блогу')}
                </Link>
                <span style={{ font: "400 14px 'Golos Text', sans-serif", color: '#9aa88c' }}>
                  {t('blog.breadcrumb_short', 'Блог')}
                  {post.category ? ` · ${post.category}` : ''}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                {post.category && <span className="chip">{post.category}</span>}
                <span style={{ font: "400 13px 'Golos Text', sans-serif", color: '#9aa88c' }}>
                  {fmtDate(post.publishedAt)}
                </span>
              </div>

              <h1
                style={{
                  font: "700 38px/1.18 'Montserrat Alternates', sans-serif",
                  color: '#2D5016',
                  margin: '0 0 18px'
                }}
              >
                {title(post)}
              </h1>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 26 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: '#DFEFCB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    font: "700 14px 'Montserrat Alternates', sans-serif",
                    color: '#0B7A43'
                  }}
                >
                  {(() => {
                    const name = post.authorName || 'Жашыл Дем';
                    const parts = name.trim().split(/\s+/).filter(Boolean);
                    const initials = parts.length >= 2
                      ? (parts[0][0] + parts[1][0])
                      : (name[0] + (name[1] || ''));
                    return initials.toUpperCase().slice(0, 2);
                  })()}
                </div>
                <div>
                  <div style={{ font: "600 14px 'Golos Text', sans-serif" }}>
                    {post.authorName || t('blog.author_name', 'Команда Жашыл Дем')}
                  </div>
                  <div style={{ font: "400 12.5px 'Golos Text', sans-serif", color: '#9aa88c' }}>
                    {post.authorRole || t('blog.author_role', 'Редакция проекта')}
                  </div>
                </div>
              </div>

              {post.coverImage && (
                <figure style={{ margin: '28px 0' }}>
                  <img
                    src={post.coverImage.startsWith('http') ? post.coverImage : `${API_URL}${post.coverImage}`}
                    alt=""
                    style={{ width: '100%', borderRadius: 22, display: 'block' }}
                  />
                </figure>
              )}

              {excerptText && (
                <p className="blog-article-lead">{excerptText}</p>
              )}

              <div
                className="blog-article-body"
                dangerouslySetInnerHTML={{ __html: contentHtml || '' }}
              />

              <div style={{ display: 'flex', gap: 10, marginTop: 36, flexWrap: 'wrap' }}>
                <Link to="/#hero" className="pill-btn" style={{ fontSize: 16, padding: '14px 28px' }}>
                  {t('header.donate')}
                </Link>
                <Link to="/blog" className="back-btn" style={{ padding: '14px 26px' }}>
                  ← {t('blog.back_to_list', 'Все статьи')}
                </Link>
              </div>
            </article>

            {related.length > 0 && (
              <div style={{ maxWidth: 1100, margin: '64px auto 0' }}>
                <div
                  style={{
                    font: "700 24px 'Montserrat Alternates', sans-serif",
                    color: '#2D5016',
                    marginBottom: 22
                  }}
                >
                  {t('blog.related', 'Читайте также')}
                </div>
                <div className="grid-3" style={{ gap: 24 }}>
                  {related.map(p => (
                    <Link key={p.slug} to={`/blog/${p.slug}`} className="blog-card">
                      <div style={{ aspectRatio: '16 / 10', position: 'relative' }}>
                        {p.coverImage ? (
                          <img
                            src={p.coverImage.startsWith('http') ? p.coverImage : `${API_URL}${p.coverImage}`}
                            alt=""
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div style={{ position: 'absolute', inset: 0, background: '#DFEFCB' }} />
                        )}
                      </div>
                      <div style={{ padding: '18px 20px' }}>
                        {p.category && <span className="chip">{p.category}</span>}
                        <div
                          style={{
                            font: "700 17px/1.3 'Montserrat Alternates', sans-serif",
                            color: '#2D5016',
                            marginTop: 10
                          }}
                        >
                          {title(p)}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </>
  );
};

export default BlogPost;
